import { lazy, Suspense, useEffect } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAlertStore } from './store/alertStore'
import { useLiveAlerts } from './hooks/useLiveAlerts'
import { useIsDesktop } from './hooks/useIsDesktop'
import { ProtectedRoute } from './components/ProtectedRoute'
import PwaInstallBanner from './components/PwaInstallBanner'
import AlertMap from './components/map/AlertMap'
import AlertLayer from './components/map/AlertLayer'
import Sidebar from './components/layout/Sidebar'
import MobileSheet from './components/layout/MobileSheet'

const AdminPage = lazy(() => import('./pages/AdminPage'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 2, staleTime: 30_000 },
  },
})

function NotificationClickHandler() {
  const selectAlert = useAlertStore((s) => s.selectAlert)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'alert.notification.click' && event.data.alertId) {
        selectAlert(event.data.alertId as string)
      }
    }
    navigator.serviceWorker.addEventListener('message', handler)
    return () => navigator.serviceWorker.removeEventListener('message', handler)
  }, [selectAlert])
  return null
}

function MapPage() {
  const filters = useAlertStore((s) => s.filters)
  const isDesktop = useIsDesktop()

  const wsFilter = {
    hazard_types: filters.hazard_types.length > 0 ? filters.hazard_types : undefined,
    min_priority: filters.min_priority > 1 ? filters.min_priority : undefined,
  }

  useLiveAlerts(wsFilter)

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {})
    }
  }, [])

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-gray-50 dark:bg-gray-950">
      {isDesktop ? (
        <div className="flex h-full w-full">
          <Sidebar />
          <AlertMap className="flex-1">
            <AlertLayer />
          </AlertMap>
        </div>
      ) : (
        <div className="flex h-full w-full relative">
          <AlertMap className="flex-1">
            <AlertLayer />
          </AlertMap>
          <MobileSheet />
        </div>
      )}
    </div>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <PwaInstallBanner />
      <NotificationClickHandler />
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MapPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<div className="min-h-screen bg-gray-50 dark:bg-gray-950" />}>
                <AdminPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </QueryClientProvider>
  )
}
